import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { GradientText, SectionLabel } from "./GradientText";
import { Play } from "lucide-react";

interface AppDemoVideoProps {
  videoId: string;
  thumbnail: string;
}

/**
 * AppDemoVideo
 *
 * Video section with thumbnail and play button overlay.
 * Clicking loads the YouTube embed with autoplay.
 * Text from translations.
 */
export function AppDemoVideo({ videoId, thumbnail }: AppDemoVideoProps) {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const title = t("appDemoVideo.title");
  const description = t("appDemoVideo.description");

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "mx-auto mb-16 max-w-3xl text-center lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <SectionLabel className="mb-6">Demo</SectionLabel>

          <h2 className="mb-6 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            <GradientText>{title}</GradientText>
          </h2>

          {description && (
            <p className="text-base leading-relaxed text-muted-foreground lg:text-lg">
              {description}
            </p>
          )}
        </div>

        {/* Video container */}
        <div
          className={cn(
            "relative mx-auto max-w-4xl",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
          style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
        >
          <div
            className={cn(
              "relative aspect-video overflow-hidden rounded-2xl",
              "border border-border bg-muted",
              "shadow-2xl shadow-black/10",
            )}
          >
            {isPlaying ? (
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                title={title}
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                {/* Thumbnail */}
                <div className="absolute inset-0 bg-muted">
                  <img
                    src={thumbnail}
                    alt={title}
                    className="h-full w-full object-cover opacity-80"
                    onError={(e) => {
                      // Fallback gradient background
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>

                {/* Play button */}
                <button
                  onClick={handlePlay}
                  className={cn(
                    "absolute inset-0 flex items-center justify-center",
                    "group cursor-pointer",
                  )}
                  aria-label="Play video"
                >
                  <div
                    className={cn(
                      "flex h-20 w-20 items-center justify-center rounded-full",
                      "bg-primary text-primary-foreground",
                      "shadow-lg shadow-primary/30",
                      "transition-all duration-300",
                      "group-hover:scale-110 group-hover:shadow-xl group-hover:shadow-primary/40",
                    )}
                  >
                    <Play className="ml-1 h-8 w-8" fill="currentColor" />
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
